<template>
  <DialogHeader>
    <div
      class="size-[30px] rounded-full bg-red-200/50 flex items-center justify-center mr-2.5"
    >
      <Icon name="Alert" :size="16" class="stroke-red-700" />
    </div>
    <div>Are you sure?</div>
  </DialogHeader>
  <DialogContent>
    <div class="mb-3">
      Please confirm you wish to archive
      <span class="font-medium">
        {{ filesCopy }}
      </span>
      from Dacr.
    </div>
    <div>
      You will not be able to recover
      {{ pluralize('this copyright', files.length) }}.
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-full tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      danger
      class="w-full tablet-h:w-auto"
      :loading="loading"
      @click="handleClick"
    >
      Confirm Archive
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { Icon, Table, UiButton } from '@dacr/ui'
import {
  useArchiveFilesMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import type { File } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { removeItemsFromExplorerCache } from '@/features/explorer/utils/explorerCache'
import gql from 'graphql-tag'
import { computed } from 'vue'
import pluralize from 'pluralize'
import { TableTypes } from '@/shared/types/index'
import { useRoute, useRouter } from 'vue-router'

const { close, open } = useDialog()

const props = defineProps<{
  files: File[]
}>()

const { mutate: archiveFiles, loading } = useArchiveFilesMutation()

const route = useRoute()
const router = useRouter()
const table = Table.useTable(TableTypes.Explorer)
const userStore = useUserStore()

const filesCopy = computed(() => {
  if (props.files.length === 1) return props.files[0].name
  return `${props.files.length} copyrights`
})

const handleClick = async () => {
  try {
    const fileIdsToArchive = props.files.map((file) => file.id)

    const response = await archiveFiles(
      {
        fileIds: fileIdsToArchive,
      },
      {
        update() {
          removeItemsFromExplorerCache(fileIdsToArchive)
        },
      }
    )

    table.resetSelection()

    if (response?.data?.archiveFiles.asyncProcess) {
      open(Dialogs.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()
    }

    if (route.path.includes('file')) {
      router.push('/')
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('license')) {
      open(Dialogs.Error, {
        props: {
          title: 'Cannot archive licenses',
          content: 'You cannot archive license copyrights at this time.',
          error,
        },
      })
    }
  }
}
</script>
